import { advertiserReducer } from "advertiser/store/reducer"
import { getDefaultAdvertiserState } from "advertiser/store/state"
import { agencyReducer } from "agency/store/reducer"
import { getDefaultAgencyState } from "agency/store/state"
import { appEventModule } from "app/event"
import { languageReducer } from "app/language/store/reducer"
import { getDefaultLanguageState } from "app/language/store/state"
import { APP_EVENTS, IRootState } from "app/model"
import { audienceReducer } from "audience/store/reducer"
import { getDefaultAudienceState } from "audience/store/state"
import { campaignReducer } from "campaign/store/reducer"
import { getDefaultCampaignState } from "campaign/store/state"
import { currencyReducer } from "currency/store/reducer"
import { getDefaultCurrencyState } from "currency/store/state"
import { dspReducer } from "dsp/store/reducer"
import { getDefaultDspState } from "dsp/store/state"
import { environmentReducer } from "environments/store/reducer"
import { getDefaultEnvironmentState } from "environments/store/state"
import { formReducer } from "form/store/reducer"
import { getDefaultFormState } from "form/store/state"
import { formatReducer } from "format/store/reducer"
import { getDefaultFormatState } from "format/store/state"
import { mediaReducer } from "media/store/reducer"
import { getDefaultMediaState } from "media/store/state"
import { messageReducer } from "message/store/reducer"
import { getDefaultMessageState } from "message/store/state"
import { notificationReducer } from "notification/store/reducer"
import { getDefaultNotificationState } from "notification/store/state"
import { orderReducer } from "order/store/reducer"
import { getDefaultOrderState } from "order/store/state"
import { pageReducer } from "page/store/reducer"
import { getDefaultPageState } from "page/store/state"
import { Action, Reducer, Store, StoreEnhancer, compose, createStore } from "redux"
import { reportsReducer } from "reports/store/reducer"
import { getDefaultReportsState } from "reports/store/state"
import { scheduleReducer } from "schedule/store/reducer"
import { getDefaultScheduleState } from "schedule/store/state"
import { sspReducer } from "ssp/store/reducer"
import { getDefaultSspState } from "ssp/store/state"
import { uiReducer } from "ui/store/reducer"
import { getDefaultUiState } from "ui/store/state"
import { tagReducer } from "user-tag/store/reducer"
import { getDefaultTagState } from "user-tag/store/state"
import { userReducer } from "user/store/reducer"
import { getDefaultUserState } from "user/store/state"
import { ACTIONS } from "./actions"
import { appReducer } from "./reducer"
import { getDefaultAppState } from "./state"

const getDefaultRootState = (): IRootState => ({
  app: getDefaultAppState(),
  ui: getDefaultUiState(),
  language: getDefaultLanguageState(),
  user: getDefaultUserState(),
  message: getDefaultMessageState(),
  page: getDefaultPageState(),
  campaign: getDefaultCampaignState(),
  agency: getDefaultAgencyState(),
  order: getDefaultOrderState(),
  advertiser: getDefaultAdvertiserState(),
  currency: getDefaultCurrencyState(),
  audience: getDefaultAudienceState(),
  environments: getDefaultEnvironmentState(),
  ssp: getDefaultSspState(),
  form: getDefaultFormState(),
  media: getDefaultMediaState(),
  tag: getDefaultTagState(),
  format: getDefaultFormatState(),
  reports: getDefaultReportsState(),
  schedule: getDefaultScheduleState(),
  notification: getDefaultNotificationState(),
  dsp: getDefaultDspState()
})

const rootState: IRootState = getDefaultRootState()

const rootReducer: Reducer<IRootState, Action> = (state: IRootState | undefined, action: Action): IRootState => {
  if (!state) {
    state = rootState
  }
  if (action.type === ACTIONS.RESET) {
    return getDefaultRootState()
  }
  return {
    app: appReducer(state.app, action),
    ui: uiReducer(state.ui, action),
    language: languageReducer(state.language, action),
    user: userReducer(state.user, action),
    message: messageReducer(state.message, action),
    page: pageReducer(state.page, action),
    campaign: campaignReducer(state.campaign, action),
    agency: agencyReducer(state.agency, action),
    order: orderReducer(state.order, action),
    advertiser: advertiserReducer(state.advertiser, action),
    currency: currencyReducer(state.currency, action),
    audience: audienceReducer(state.audience, action),
    environments: environmentReducer(state.environments, action),
    ssp: sspReducer(state.ssp, action),
    form: formReducer(state.form, action),
    media: mediaReducer(state.media, action),
    tag: tagReducer(state.tag, action),
    format: formatReducer(state.format, action),
    reports: reportsReducer(state.reports, action),
    schedule: scheduleReducer(state.schedule, action),
    notification: notificationReducer(state.notification, action),
    dsp: dspReducer(state.dsp, action)
  }
}

const hasReduxDevtool: boolean = window.hasOwnProperty("__REDUX_DEVTOOLS_EXTENSION_COMPOSE__")
const composeEnhancers = hasReduxDevtool
  ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      /* opt */
    })
  : compose

const enhancer: StoreEnhancer = composeEnhancers()

const store: Store<IRootState, Action> = createStore(rootReducer, rootState, enhancer)

export function getStore(): Store<IRootState, Action> {
  return store
}

appEventModule.notify(APP_EVENTS.STORE_READY, {})
